import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sparkles, BookOpen, AlertCircle, ListChecks, Quote, ClipboardList,
  Tag, Calendar, Camera, Lightbulb, X, Loader2, Copy, Send,
  ChevronLeft, ChevronRight, Wand2, ImagePlus, Layers
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  Sparkles, BookOpen, AlertCircle, ListChecks, Quote, ClipboardList,
  Tag, Calendar, Camera, Lightbulb,
};

const CATEGORY_LABEL = {
  case: { txt: "Caso real", color: "#D97757", bg: "#FBE9E1" },
  educational: { txt: "Educativo", color: "#7A9180", bg: "#E8EFE7" },
  promotional: { txt: "Promo", color: "#A69076", bg: "#F4EFE6" },
  social_proof: { txt: "Testimonio", color: "#5C5E5C", bg: "#EFECEC" },
  informational: { txt: "Info", color: "#1A1C1A", bg: "#E8E5E0" },
  operational: { txt: "Agenda", color: "#A69076", bg: "#F4EFE6" },
  lifestyle: { txt: "Lifestyle", color: "#D97757", bg: "#FBE9E1" },
};

export default function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [variables, setVariables] = useState({});
  const [includeImage, setIncludeImage] = useState(true);
  const [userImage, setUserImage] = useState(null);  // base64 data URL of uploaded photo
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [scheduleFor, setScheduleFor] = useState("");
  const [caseRealOpen, setCaseRealOpen] = useState(false);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => toast.error("Error cargando plantillas"));
  }, []);

  const openTemplate = (t) => {
    // Special handling for caso_real → dedicated editor with real photos
    if (t.id === "caso_real") {
      setCaseRealOpen(true);
      return;
    }
    setSelected(t);
    const initial = {};
    t.variables.forEach((v) => { initial[v.key] = ""; });
    setVariables(initial);
    setResult(null);
    setUserImage(null);
  };

  const closeTemplate = () => {
    setSelected(null);
    setVariables({});
    setResult(null);
    setScheduleFor("");
    setUserImage(null);
  };

  const handleImageUpload = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagen muy grande (máx 8 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setUserImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    // Validate all vars filled
    for (const v of selected.variables) {
      if (!variables[v.key]?.trim()) {
        toast.error(`Falta: ${v.label}`);
        return;
      }
    }
    setGenerating(true);
    try {
      const r = await api.post("/templates/generate", {
        template_id: selected.id,
        variables,
        include_image: includeImage,
        user_image_base64: userImage,
      });
      setResult(r.data);
      toast.success(userImage
        ? "¡Plantilla lista! Tu foto fue adaptada al mood de la marca."
        : "¡Plantilla generada! Ya está como borrador.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando");
    } finally {
      setGenerating(false);
    }
  };

  const copyText = () => {
    const txt = `${result.caption}\n\n${(result.hashtags || []).map(h => "#" + h).join(" ")}`;
    navigator.clipboard.writeText(txt);
    toast.success("Texto copiado");
  };

  const schedule = async () => {
    if (!scheduleFor) { toast.error("Elige fecha y hora"); return; }
    try {
      await api.patch(`/social-posts/${result.id}/schedule`, { scheduled_for: new Date(scheduleFor).toISOString() });
      toast.success("Programado. Se publicará automáticamente.");
      closeTemplate();
    } catch { toast.error("Error al programar"); }
  };

  if (selected) {
    return (
      <TemplateEditor
        template={selected}
        variables={variables}
        setVariables={setVariables}
        includeImage={includeImage}
        setIncludeImage={setIncludeImage}
        userImage={userImage}
        setUserImage={setUserImage}
        onUpload={handleImageUpload}
        generating={generating}
        result={result}
        scheduleFor={scheduleFor}
        setScheduleFor={setScheduleFor}
        onGenerate={generate}
        onCopy={copyText}
        onSchedule={schedule}
        onBack={closeTemplate}
      />
    );
  }

  if (caseRealOpen) {
    return <CaseRealEditor onBack={() => setCaseRealOpen(false)} />;
  }

  return (
    <div className="space-y-6" data-testid="templates-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Plantillas IG</div>
          <h3 className="font-display text-2xl text-[#1A1C1A]">10 Plantillas Listas para Publicar</h3>
          <p className="text-xs text-[#5C5E5C] mt-1 max-w-xl">
            Cada plantilla genera caption + hashtags + imagen con el mood de la marca. Se guarda como borrador para que la edites o programes.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {templates.map((t) => {
          const Icon = ICONS[t.icon] || Sparkles;
          const cat = CATEGORY_LABEL[t.category] || { txt: t.category, color: "#5C5E5C", bg: "#F0EDE8" };
          return (
            <button
              key={t.id}
              onClick={() => openTemplate(t)}
              className="text-left bg-white rounded-2xl border border-[#E6E2DC] hover:border-[#D97757] hover:-translate-y-1 hover:shadow-xl transition-all p-6 group relative"
              data-testid={`tpl-card-${t.id}`}
            >
              {t.is_carousel && (
                <span className="absolute top-3 right-3 text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-[#D97757] text-white font-bold">
                  {t.slides_count} slides
                </span>
              )}
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#F4F0EB] border border-[#E6E2DC] flex items-center justify-center group-hover:bg-[#FBE9E1] group-hover:border-[#D97757] transition-colors">
                  <Icon className="w-5 h-5 text-[#A69076] group-hover:text-[#D97757] transition-colors" />
                </div>
                <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full" style={{ background: cat.bg, color: cat.color }}>
                  {cat.txt}
                </span>
              </div>
              <h4 className="font-display text-lg text-[#1A1C1A] leading-tight mb-2">{t.name}</h4>
              <p className="text-xs text-[#5C5E5C] leading-relaxed line-clamp-3">{t.description}</p>
              <div className="mt-4 flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#D97757] opacity-0 group-hover:opacity-100 transition-opacity">
                <Wand2 className="w-3 h-3" /> Usar plantilla
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TemplateEditor({
  template, variables, setVariables, includeImage, setIncludeImage,
  userImage, setUserImage, onUpload,
  generating, result, scheduleFor, setScheduleFor,
  onGenerate, onCopy, onSchedule, onBack,
}) {
  const Icon = ICONS[template.icon] || Sparkles;
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-xs text-[#5C5E5C] hover:text-[#1A1C1A] inline-flex items-center gap-1.5" data-testid="tpl-back">
        <ChevronLeft className="w-4 h-4" /> Volver a plantillas
      </button>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-7 h-fit">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#FBE9E1] border border-[#D97757] flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#D97757]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Plantilla</div>
              <h3 className="font-display text-2xl text-[#1A1C1A] leading-tight">{template.name}</h3>
              <p className="text-xs text-[#5C5E5C] mt-2">{template.description}</p>
            </div>
          </div>

          {template.variables.length === 0 && (
            <p className="text-sm text-[#5C5E5C] mb-4 italic">Esta plantilla no requiere variables adicionales.</p>
          )}
          {template.variables.map((v) => (
            <div key={v.key} className="mb-4">
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">{v.label}</Label>
              {v.type === "textarea" ? (
                <Textarea
                  rows={3}
                  placeholder={v.placeholder}
                  value={variables[v.key] || ""}
                  onChange={(e) => setVariables({ ...variables, [v.key]: e.target.value })}
                  data-testid={`tpl-input-${v.key}`}
                />
              ) : (
                <Input
                  placeholder={v.placeholder}
                  value={variables[v.key] || ""}
                  onChange={(e) => setVariables({ ...variables, [v.key]: e.target.value })}
                  data-testid={`tpl-input-${v.key}`}
                />
              )}
            </div>
          ))}

          <label className="flex items-center gap-2 mt-5 text-xs text-[#1A1C1A] cursor-pointer">
            <input
              type="checkbox"
              checked={includeImage}
              onChange={(e) => setIncludeImage(e.target.checked)}
              className="accent-[#D97757]"
              data-testid="tpl-include-image"
            />
            Incluir imagen en la publicación
          </label>

          {/* Upload de foto propia (opcional) — solo en posts simples */}
          {includeImage && !template.is_carousel && (
            <div className="mt-4 bg-[#F4F0EB] border border-[#E6E2DC] rounded-xl p-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#A69076] mb-2 flex items-center gap-1.5">
                <Wand2 className="w-3 h-3" /> Editor inteligente
              </div>
              {!userImage ? (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#A69076] hover:border-[#D97757] rounded-lg p-6 cursor-pointer transition-colors group" data-testid="tpl-upload-zone">
                  <ImagePlus className="w-6 h-6 text-[#A69076] group-hover:text-[#D97757]" />
                  <div className="text-xs text-[#1A1C1A] font-medium">Sube tu foto</div>
                  <div className="text-[10px] text-[#5C5E5C] text-center max-w-[220px] leading-relaxed">
                    La adaptamos al mood de la marca (paleta, luz, fondo limpio) sin cambiar a la persona.
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onUpload(e.target.files?.[0])}
                    data-testid="tpl-upload-input"
                  />
                  <div className="text-[10px] text-[#A69076] mt-1">o deja vacío para generar con IA desde cero</div>
                </label>
              ) : (
                <div className="relative" data-testid="tpl-upload-preview">
                  <img src={userImage} alt="" className="w-full aspect-square object-cover rounded-lg" />
                  <button
                    onClick={() => setUserImage(null)}
                    className="absolute top-2 right-2 p-1.5 bg-white border border-[#E6E2DC] rounded-full hover:bg-[#F5E0E0] hover:border-[#965D5D] transition-colors"
                    data-testid="tpl-upload-remove"
                  >
                    <X className="w-3 h-3 text-[#965D5D]" />
                  </button>
                  <div className="mt-2 text-[10px] text-[#1A1C1A] flex items-center gap-1.5">
                    <Wand2 className="w-3 h-3 text-[#D97757]" />
                    <span><strong>Foto cargada.</strong> Al generar, pasará por el filtro de marca (paleta + luz + fondo limpio).</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Carousel info card */}
          {template.is_carousel && (
            <div className="mt-5 bg-gradient-to-br from-[#FBE9E1] to-[#F4F0EB] border border-[#D97757] rounded-xl p-4 flex items-start gap-3">
              <Layers className="w-5 h-5 text-[#D97757] mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-medium text-[#1A1C1A] mb-1">Carrusel de 7 slides</div>
                <div className="text-[10px] text-[#5C5E5C] leading-relaxed">
                  Generaremos 7 imágenes estilo editorial + caption principal. Estrategia probada en clínicas top (@dr.carlabarber) para ganar saves + shares.
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={onGenerate}
            disabled={generating}
            className="w-full mt-6 rounded-full bg-[#1A1C1A] hover:bg-[#D97757] text-white h-11 text-xs uppercase tracking-[0.2em]"
            data-testid="tpl-generate"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {userImage ? "Adaptando tu foto…" : "Generando…"}</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> {userImage ? "Adaptar mi foto + generar caption" : "Generar publicación"}</>
            )}
          </Button>
          <p className="text-[10px] text-[#A69076] mt-2 italic flex items-center gap-1">
            <Wand2 className="w-3 h-3" />
            {userImage
              ? "Editor inteligente: re-estila tu foto a la paleta beige/coral sin cambiar a la persona."
              : "Editor inteligente: adapta texto + imagen al mood de la clínica."}
          </p>
        </div>

        {/* Preview / Result */}
        <div className="bg-[#F4F0EB] rounded-2xl border border-[#E6E2DC] p-7">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-3">Preview Instagram</div>
          {!result && (
            <div className="aspect-square rounded-xl border-2 border-dashed border-[#E6E2DC] flex flex-col items-center justify-center text-center px-6">
              <Wand2 className="w-8 h-8 text-[#A69076] mb-3" />
              <p className="text-xs text-[#5C5E5C] max-w-xs">Completa los campos y genera la publicación. Aquí verás un preview tipo Instagram con el resultado.</p>
            </div>
          )}
          {result && (
            <div className="bg-white rounded-xl overflow-hidden border border-[#E6E2DC] shadow-sm" data-testid="tpl-preview">
              {/* IG Header */}
              <div className="flex items-center gap-2.5 p-3 border-b border-[#F0EDE8]">
                <div className="w-8 h-8 rounded-full border-2 border-[#D97757] p-0.5">
                  <div className="w-full h-full rounded-full bg-[#1A1C1A] flex items-center justify-center">
                    <span className="font-display text-[10px] text-[#A69076]">tm</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-[#1A1C1A] leading-tight">dra.taniamendoza</div>
                  <div className="text-[9px] text-[#5C5E5C]">Pachuca, Hidalgo</div>
                </div>
                {result.is_carousel && (
                  <span className="ml-auto text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-[#D97757] text-white">
                    Carrusel · {result.slides?.length || 0}
                  </span>
                )}
              </div>
              {/* Image or Carousel */}
              {result.is_carousel ? (
                <CarouselPreview slides={result.slides || []} />
              ) : result.image_base64 ? (
                <img src={result.image_base64} alt="" className="aspect-square w-full object-cover" />
              ) : (
                <div className="aspect-square bg-[#F4F0EB] flex items-center justify-center">
                  <p className="text-xs text-[#5C5E5C] italic">Sin imagen</p>
                </div>
              )}
              {/* Caption */}
              <div className="p-4">
                <p className="text-xs text-[#1A1C1A] leading-relaxed whitespace-pre-line">{result.caption}</p>
                {result.hashtags?.length > 0 && (
                  <p className="text-xs text-[#7A9180] mt-2 leading-relaxed">
                    {result.hashtags.map(h => "#" + h).join(" ")}
                  </p>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="mt-5 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={onCopy} className="text-xs"><Copy className="w-3 h-3 mr-1" /> Copiar texto</Button>
              </div>
              <div className="bg-white rounded-xl border border-[#E6E2DC] p-4">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Programar publicación automática</Label>
                <div className="flex gap-2">
                  <Input
                    type="datetime-local"
                    className="h-9 text-xs flex-1"
                    min={new Date().toISOString().slice(0, 16)}
                    value={scheduleFor}
                    onChange={(e) => setScheduleFor(e.target.value)}
                    data-testid="tpl-schedule-input"
                  />
                  <Button size="sm" onClick={onSchedule} className="bg-[#D97757] hover:bg-[#1A1C1A] text-white text-xs" data-testid="tpl-schedule">
                    <Send className="w-3 h-3 mr-1" /> Programar
                  </Button>
                </div>
                <p className="text-[10px] text-[#A69076] mt-2">El scheduler lo subirá automáticamente a IG + FB en la fecha indicada.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CarouselPreview({ slides }) {
  const [idx, setIdx] = useState(0);
  if (!slides.length) return <div className="aspect-square bg-[#F4F0EB]" />;
  const current = slides[idx];
  return (
    <div className="relative aspect-square w-full bg-[#1A1C1A] group">
      {current.image_base64 ? (
        <img src={current.image_base64} alt={`Slide ${idx + 1}`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/60 text-xs">Sin imagen</div>
      )}
      {idx > 0 && (
        <button onClick={() => setIdx(idx - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" data-testid="carousel-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      {idx < slides.length - 1 && (
        <button onClick={() => setIdx(idx + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity" data-testid="carousel-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-1">
        {slides.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/50 w-1.5"}`} data-testid={`carousel-dot-${i}`} />
        ))}
      </div>
    </div>
  );
}




// -----------------------------------------------------------------------------
// CaseRealEditor — dedicated flow for real before/after with brand style overlay
// -----------------------------------------------------------------------------
function CaseRealEditor({ onBack }) {
  const [mode, setMode] = useState("gallery"); // gallery | upload
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [beforeImg, setBeforeImg] = useState("");
  const [afterImg, setAfterImg] = useState("");
  const [palettes, setPalettes] = useState([]);
  const [paletteId, setPaletteId] = useState("latte_coral");
  const [layout, setLayout] = useState("vertical");
  const [keepOriginalBg, setKeepOriginalBg] = useState(false);
  const [treatment, setTreatment] = useState("");
  const [patientNote, setPatientNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [scheduleFor, setScheduleFor] = useState("");

  useEffect(() => {
    api.get("/gallery")
      .then((r) => setGalleryItems((r.data || []).filter((g) => g.is_real && g.before_image && g.after_image)))
      .catch(() => toast.error("Error cargando galería"))
      .finally(() => setGalleryLoading(false));
    api.get("/brand/palettes")
      .then((r) => {
        setPalettes(r.data.palettes || []);
        setPaletteId(r.data.default || "latte_coral");
      })
      .catch(() => { /* palette list is nice-to-have */ });
  }, []);

  const fileToB64 = (file) => new Promise((resolve, reject) => {
    if (file.size > 8 * 1024 * 1024) { reject(new Error("Máx 8 MB")); return; }
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleBeforeUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setBeforeImg(await fileToB64(f)); }
    catch (err) { toast.error(err.message); }
  };
  const handleAfterUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setAfterImg(await fileToB64(f)); }
    catch (err) { toast.error(err.message); }
  };

  // Clean burned-in text/watermarks from an uploaded case image + suggest fresh copy
  const [cleaningSide, setCleaningSide] = useState(null); // "before" | "after" | null
  const [cleanSuggestion, setCleanSuggestion] = useState(null); // { hook, caption_line, side }
  const cleanImageText = async (side) => {
    const b64 = side === "before" ? beforeImg : afterImg;
    if (!b64) { toast.error("Sube primero la imagen"); return; }
    setCleaningSide(side);
    try {
      const r = await api.post("/case-real/clean-image",
        { image_base64: b64, side, suggest_copy: true, context: treatment || patientNote || null },
        { timeout: 3 * 60 * 1000 });
      if (side === "before") setBeforeImg(r.data.clean_image_base64);
      else setAfterImg(r.data.clean_image_base64);
      const sug = r.data.suggestion;
      if (sug?.hook) {
        setCleanSuggestion({ ...sug, side });
        toast.success("Texto quitado + gancho sugerido");
      } else {
        toast.success("Texto quitado");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo limpiar la imagen");
    } finally { setCleaningSide(null); }
  };
  const applySuggestion = () => {
    if (!cleanSuggestion) return;
    // Populate the treatment/note fields if empty (non-destructive)
    if (!patientNote.trim() && cleanSuggestion.caption_line) setPatientNote(cleanSuggestion.caption_line);
    if (!treatment.trim() && cleanSuggestion.service_hint && cleanSuggestion.service_hint !== "otro") {
      const map = { ortodoncia: "Ortodoncia", estetica: "Medicina estética", botox: "Botox", skincare: "Skincare" };
      setTreatment(map[cleanSuggestion.service_hint] || "");
    }
    toast.success("Sugerencia aplicada");
    setCleanSuggestion(null);
  };

  const canGenerate = () => {
    if (!treatment.trim() || !patientNote.trim()) return false;
    if (mode === "gallery") return !!selectedGalleryId;
    return !!(beforeImg && afterImg);
  };

  const generate = async () => {
    if (!canGenerate()) { toast.error("Faltan datos o fotos"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const body = {
        treatment, patient_note: patientNote,
        palette_id: paletteId, layout,
        keep_original_bg: keepOriginalBg,
      };
      if (mode === "gallery") {
        body.gallery_item_id = selectedGalleryId;
      } else {
        body.before_image_base64 = beforeImg;
        body.after_image_base64 = afterImg;
      }
      const r = await api.post("/case-real/generate", body, { timeout: 5 * 60 * 1000 });
      setResult(r.data);
      toast.success("Listo. Revisa la vista previa y agenda si te gusta.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando");
    } finally {
      setGenerating(false);
    }
  };

  const schedule = async () => {
    if (!result?.id || !scheduleFor) { toast.error("Elige fecha y hora"); return; }
    try {
      await api.patch(`/social-posts/${result.id}/schedule`, {
        scheduled_for: new Date(scheduleFor).toISOString(),
      });
      toast.success("Programado. Se publicará automáticamente.");
      onBack();
    } catch { toast.error("Error programando"); }
  };

  const activePalette = palettes.find((p) => p.id === paletteId);

  return (
    <div className="space-y-6" data-testid="case-real-editor">
      <button onClick={onBack} className="text-xs text-[#5C5E5C] hover:text-[#1A1C1A] inline-flex items-center gap-1.5" data-testid="case-real-back">
        <ChevronLeft className="w-4 h-4" /> Volver a plantillas
      </button>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* --- Left: Form --- */}
        <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6 space-y-5">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Plantilla</div>
            <h3 className="font-display text-2xl text-[#1A1C1A]">Caso Real — Antes & Después</h3>
            <p className="text-xs text-[#5C5E5C] mt-1">Fotos reales del paciente + estilo de marca encima (fondo, marco, tipografía).</p>
          </div>

          {/* Source mode */}
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Fotos</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("gallery")}
                className={`h-10 text-xs rounded-lg border transition ${mode === "gallery" ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "bg-white text-[#5C5E5C] border-[#E6E2DC] hover:border-[#A69076]"}`}
                data-testid="case-real-mode-gallery"
              >De galería</button>
              <button
                type="button"
                onClick={() => setMode("upload")}
                className={`h-10 text-xs rounded-lg border transition ${mode === "upload" ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "bg-white text-[#5C5E5C] border-[#E6E2DC] hover:border-[#A69076]"}`}
                data-testid="case-real-mode-upload"
              >Subir nuevas</button>
            </div>
          </div>

          {mode === "gallery" ? (
            <div>
              {galleryLoading ? (
                <p className="text-xs text-[#A69076] italic">Cargando galería…</p>
              ) : galleryItems.length === 0 ? (
                <p className="text-xs text-[#965D5D] italic">No hay items reales con antes+después. Sube uno en Admin → Galería o usa modo &quot;Subir nuevas&quot;.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
                  {galleryItems.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setSelectedGalleryId(g.id)}
                      className={`relative rounded overflow-hidden border-2 transition ${selectedGalleryId === g.id ? "border-[#D97757] shadow-md" : "border-transparent hover:border-[#A69076]"}`}
                      data-testid={`case-real-gallery-${g.id}`}
                      title={g.title}
                    >
                      <div className="grid grid-cols-2">
                        <img src={g.before_image} alt="" className="w-full h-16 object-cover" />
                        <img src={g.after_image} alt="" className="w-full h-16 object-cover" />
                      </div>
                      <div className="text-[9px] text-[#5C5E5C] px-1 py-0.5 truncate text-left">{g.title}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="block cursor-pointer bg-[#F4F0EB] border border-dashed border-[#E6E2DC] rounded-lg p-3 text-center hover:border-[#D97757] transition">
                  <div className="text-[10px] tracking-widest uppercase text-[#A69076] mb-1">Antes</div>
                  {beforeImg ? (
                    <img src={beforeImg} alt="" className="w-full aspect-square object-cover rounded" />
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center text-[#A69076]">
                      <ImagePlus className="w-6 h-6" />
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleBeforeUpload} className="hidden" data-testid="case-real-upload-before" />
                </label>
                {beforeImg && (
                  <button
                    type="button"
                    onClick={() => cleanImageText("before")}
                    disabled={cleaningSide === "before"}
                    data-testid="case-real-clean-before"
                    className="w-full text-[10px] tracking-widest uppercase text-[#D97757] border border-[#D97757] rounded-full py-1.5 hover:bg-[#FBE9E1] transition disabled:opacity-50 inline-flex items-center justify-center gap-1"
                  >
                    {cleaningSide === "before"
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Limpiando…</>
                      : <><Sparkles className="w-3 h-3" /> Quitar texto</>}
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="block cursor-pointer bg-[#F4F0EB] border border-dashed border-[#E6E2DC] rounded-lg p-3 text-center hover:border-[#D97757] transition">
                  <div className="text-[10px] tracking-widest uppercase text-[#A69076] mb-1">Después</div>
                  {afterImg ? (
                    <img src={afterImg} alt="" className="w-full aspect-square object-cover rounded" />
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center text-[#A69076]">
                      <ImagePlus className="w-6 h-6" />
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleAfterUpload} className="hidden" data-testid="case-real-upload-after" />
                </label>
                {afterImg && (
                  <button
                    type="button"
                    onClick={() => cleanImageText("after")}
                    disabled={cleaningSide === "after"}
                    data-testid="case-real-clean-after"
                    className="w-full text-[10px] tracking-widest uppercase text-[#D97757] border border-[#D97757] rounded-full py-1.5 hover:bg-[#FBE9E1] transition disabled:opacity-50 inline-flex items-center justify-center gap-1"
                  >
                    {cleaningSide === "after"
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Limpiando…</>
                      : <><Sparkles className="w-3 h-3" /> Quitar texto</>}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Suggested copy after cleaning */}
          {cleanSuggestion && (
            <div className="bg-[#FBE9E1] border border-[#D97757]/40 rounded-lg p-3 space-y-2" data-testid="case-real-suggestion">
              <div className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-[#D97757]">
                <Sparkles className="w-3 h-3" /> Sugerencia basada en la foto {cleanSuggestion.side === "before" ? "antes" : "después"}
              </div>
              {cleanSuggestion.hook && (
                <p className="text-sm text-[#1A1C1A] font-medium leading-snug">&ldquo;{cleanSuggestion.hook}&rdquo;</p>
              )}
              {cleanSuggestion.caption_line && (
                <p className="text-xs text-[#5C5E5C] leading-relaxed">{cleanSuggestion.caption_line}</p>
              )}
              <div className="flex gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setCleanSuggestion(null)}
                  className="text-[10px] tracking-widest uppercase text-[#5C5E5C] hover:text-[#1A1C1A] px-2 py-1"
                >
                  Ignorar
                </button>
                <button
                  type="button"
                  onClick={applySuggestion}
                  data-testid="case-real-apply-suggestion"
                  className="text-[10px] tracking-widest uppercase text-white bg-[#1A1C1A] hover:bg-[#D97757] rounded-full px-3 py-1.5 transition"
                >
                  Aplicar en el copy
                </button>
              </div>
            </div>
          )}

          {/* Palette */}
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">
              Paleta {activePalette && <span className="lowercase text-[#A69076] tracking-normal">· {activePalette.label}</span>}
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {palettes.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPaletteId(p.id)}
                  className={`flex items-center gap-1 px-2 h-8 rounded-full text-[10px] tracking-widest uppercase border transition ${paletteId === p.id ? "border-[#D97757] bg-[#FBE9E1] text-[#1A1C1A]" : "border-[#E6E2DC] text-[#5C5E5C] hover:border-[#A69076]"}`}
                  data-testid={`case-real-palette-${p.id}`}
                  title={p.label}
                >
                  <span className="w-3 h-3 rounded-full border border-white shadow-sm" style={{ background: p.neutral || p.primary }} />
                  <span className="w-3 h-3 rounded-full border border-white shadow-sm -ml-1.5" style={{ background: p.primary }} />
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Layout */}
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Composición</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setLayout("vertical")}
                className={`h-11 rounded-lg border text-xs flex items-center justify-center gap-2 transition ${layout === "vertical" ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "bg-white text-[#5C5E5C] border-[#E6E2DC] hover:border-[#A69076]"}`}
                data-testid="case-real-layout-vertical"
              >
                <Layers className="w-3.5 h-3.5" /> Vertical (arriba/abajo)
              </button>
              <button
                type="button"
                onClick={() => setLayout("horizontal")}
                className={`h-11 rounded-lg border text-xs flex items-center justify-center gap-2 transition ${layout === "horizontal" ? "bg-[#1A1C1A] text-white border-[#1A1C1A]" : "bg-white text-[#5C5E5C] border-[#E6E2DC] hover:border-[#A69076]"}`}
                data-testid="case-real-layout-horizontal"
              >
                <Layers className="w-3.5 h-3.5 rotate-90" /> Horizontal (izq/der)
              </button>
            </div>
          </div>

          {/* Keep original bg */}
          <label className="flex items-start gap-2 text-xs text-[#1A1C1A] cursor-pointer bg-[#F4F0EB] p-3 rounded-lg border border-[#E6E2DC]">
            <input
              type="checkbox"
              checked={keepOriginalBg}
              onChange={(e) => setKeepOriginalBg(e.target.checked)}
              className="accent-[#D97757] mt-0.5"
              data-testid="case-real-keep-bg"
            />
            <span className="leading-snug">
              <span className="font-medium">Mantener fondo original</span>
              <span className="block text-[10px] text-[#5C5E5C] mt-0.5">
                Si la marcas, no se reemplaza el fondo con Nano Banana. La foto queda 100% intacta y solo se agrega el marco + etiquetas.
              </span>
            </span>
          </label>

          {/* Treatment + note */}
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Tratamiento</Label>
            <Input
              placeholder="Ej. Rinomodelación sin cirugía"
              value={treatment}
              onChange={(e) => setTreatment(e.target.value)}
              data-testid="case-real-treatment"
            />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Detalle del caso</Label>
            <Textarea
              rows={3}
              placeholder="Paciente buscaba armonizar perfil sin quirófano. Se corrigió giba y se elevó la punta con ácido hialurónico."
              value={patientNote}
              onChange={(e) => setPatientNote(e.target.value)}
              data-testid="case-real-note"
            />
          </div>

          <Button
            onClick={generate}
            disabled={generating || !canGenerate()}
            className="w-full h-11 bg-[#D97757] hover:bg-[#C66749] text-white gap-2"
            data-testid="case-real-generate"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Generando (30-60s)…</>
              : <><Sparkles className="w-4 h-4" /> Generar caso real</>}
          </Button>
        </div>

        {/* --- Right: Preview / Result --- */}
        <div className="space-y-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Vista previa</div>
          {!result ? (
            <div className="aspect-square bg-[#F4F0EB] border border-dashed border-[#E6E2DC] rounded-2xl flex items-center justify-center text-xs text-[#A69076] tracking-widest uppercase text-center p-8">
              {generating ? "Preparando la imagen…" : "La imagen final aparecerá aquí"}
            </div>
          ) : (
            <div className="space-y-3">
              <img src={result.image_base64} alt="Caso real" className="w-full rounded-2xl shadow-lg" data-testid="case-real-preview" />
              {result.caption && (
                <div className="bg-white border border-[#E6E2DC] rounded-xl p-4 space-y-2">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Caption</div>
                  <p className="text-xs whitespace-pre-wrap leading-relaxed text-[#1A1C1A]">{result.caption}</p>
                  {result.hashtags?.length > 0 && (
                    <p className="text-[11px] text-[#3897F0]">{result.hashtags.map((h) => "#" + h).join(" ")}</p>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="ghost" onClick={() => {
                      navigator.clipboard.writeText(`${result.caption}\n\n${(result.hashtags || []).map((h) => "#" + h).join(" ")}`);
                      toast.success("Copiado");
                    }} className="text-[10px] h-7">
                      <Copy className="w-3 h-3 mr-1" /> Copiar
                    </Button>
                  </div>
                </div>
              )}
              <div className="bg-[#F4F0EB] border border-[#E6E2DC] rounded-xl p-3 space-y-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal">Agendar publicación (opcional)</Label>
                <Input
                  type="datetime-local"
                  value={scheduleFor}
                  onChange={(e) => setScheduleFor(e.target.value)}
                  data-testid="case-real-schedule-input"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={schedule} disabled={!scheduleFor} className="flex-1 h-9 bg-[#1A1C1A] hover:bg-[#3a3b39] text-white text-xs">
                    <Send className="w-3 h-3 mr-1" /> Agendar
                  </Button>
                  <Button size="sm" variant="outline" onClick={onBack} className="h-9 text-xs">
                    Dejar como borrador
                  </Button>
                </div>
                <p className="text-[10px] text-[#5C5E5C] italic">
                  Si no agendas, queda en Cronograma como borrador para editar después.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
